import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { NotificationRow } from "@/hooks/platform/useNotifications";

export type AlertType = "call_incoming" | "call_missed" | "live_invite" | "live_guest" | "gift";

/**
 * Delivers a push alert to another member through the secured database helper.
 * Blocked senders are dropped server-side.
 */
export async function sendAlert(args: {
  userId: string;
  type: AlertType;
  title: string;
  body?: string;
  link?: string;
  metadata?: Record<string, unknown>;
}) {
  const { error } = await supabase.rpc("send_alert", {
    _user_id: args.userId,
    _type: args.type,
    _title: args.title,
    _body: args.body ?? null,
    _link: args.link ?? null,
    _metadata: (args.metadata ?? {}) as never,
  });
  if (error) throw error;
}

/** Subscribes to my notification inbox in real time and forwards each new alert. */
export function useAlertStream(userId: string | null, onAlert: (row: NotificationRow) => void) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`alerts-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          onAlert(payload.new as NotificationRow);
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, qc]);
}
